// @flow
import * as React from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { Link } from 'react-router';
import { Grid, Row, Col, Image, Button } from 'react-bootstrap';
import { Helmet } from 'react-helmet';
import LocationSidebar from '../../components/sidebars/LocationSidebar';
import * as userCreators from '../../actions/users/userCreators';
import * as customerCreators from '../../actions/customers/customerCreators';
import { updateCurrentOrganisation } from '../../actions/organisations/currentOrganisationCreators';
import LocationTabSidebar from './LocationTabSidebar';
import LoadingSpinner from '../../components/LoadingSpinner';
import HelpWrapper from '../../components/HelpWrapper';
import FlagIcon from '../../images/flag-icon.png';
import { getOrganisationUsers } from '../../actions/organisations/userCreators';
import { getSetting } from '../../actions/settings/basicCreators';
import type { Dispatch } from 'redux';
import type { Organisation } from '../../types';

type Params = {
  locationId: number,
  customerId: number,
  context: string
};

type Props = {
  params: Params,
  router: any,
  currentLocation: Object,
  currentOrganisation: Organisation,
  currentCustomer: Organisation,
  children: any,
  updateUser: Function,
  addUser: Function,
  clearCurrentCustomerCurrentLocation: Function,
  setCurrentCustomerCurrentLocation: Function,
  updateCurrentOrganisation: Function,
  getCurrentCustomerLocations: Function,
  settings: any,
  getSetting: (s: string) => void
};

class Locations extends React.Component<Props> {
  handleSubmit = values => {
    if (values.id) {
      this.props.updateUser(values);
    } else {
      this.props.addUser(values);
    }
  };

  componentDidMount() {
    const { getSetting } = this.props;
    getSetting('subAccountDetailHelpPdf');
    getSetting('subAccountDetailHelpVideo');

    const { locationId, customerId } = this.props.params;

    if (locationId) {
      this.handleLocationIdUpdate(locationId);
    }

    if (parseInt(customerId, 10) !== this.props.currentOrganisation.id) {
      this.handleOrgIdUpdate(customerId);
    }
  }

  componentWillUnmount() {
    localStorage.removeItem('backUrl');
  }

  componentWillReceiveProps(nextProps) {
    const currentLocationId = this.props.params.locationId;
    const newCurrentLocationId = nextProps.params.locationId;

    if (newCurrentLocationId !== currentLocationId) {
      this.handleLocationIdUpdate(newCurrentLocationId);
    }

    // Update Insight Sets if currentOrganisation has changed in URL
    const currentOrgId = this.props.currentOrganisation.id;
    const newCurrentOrgId = nextProps.currentOrganisation.id;
    const customerUrlId = parseInt(this.props.params.customerId, 10);
    if (currentOrgId !== newCurrentOrgId && newCurrentOrgId !== customerUrlId) {
      this.props.getCurrentCustomerLocations(newCurrentOrgId);

      if (newCurrentOrgId) {
        // update url back to /sub-accounts
        this.props.router.push(`/customer/${newCurrentOrgId}/sub-accounts`);
      }
    }

    // if currentLocationChanges when viewing location details
    if (
      this.props.currentLocation.id !== nextProps.currentLocation.id &&
      nextProps.params.context === 'sub-account'
    ) {
      this.props.router.push('/');
    }
  }

  handleLocationIdUpdate = newId => {
    if (newId === 'new') {
      this.props.clearCurrentCustomerCurrentLocation();
      return;
    }

    this.props.setCurrentCustomerCurrentLocation(parseInt(newId, 10));
  };

  handleOrgIdUpdate = newId => {
    const orgId = parseInt(newId, 10);
    // Retry function if it fails.
    // This fixes bug when using as landing screen and data not present.
    this.props.updateCurrentOrganisation(orgId).catch(reason => {
      setInterval(() => {
        this.props.updateCurrentOrganisation(orgId);
      }, 1000);
    });
  };

  render() {
    const {
      params,
      getCurrentCustomerLocations,
      currentOrganisation,
      currentCustomer,
      children,
      settings
    } = this.props;
    const currentComponent = children ? children.type.displayName : '';
    const currentOrgId = parseInt(params.customerId, 10);
    const context = params.context;
    const backUrl = localStorage.getItem('backUrl');

    const isSubAccountContext = context === 'sub-account';
    const mainContentWidth = isSubAccountContext ? 12 : 9;

    if (currentOrganisation.id === currentOrgId) {
      return (
        <Grid fluid>
          <Helmet>
            <title>Manage Sub-Accounts | Campaign Targeter</title>
          </Helmet>
          <Row>
            {context === 'customer' && (
              <Col sm={3} className="sidebar-wrapper">
                <LocationSidebar
                  locationId={params.locationId}
                  currentOrgId={params.customerId}
                  isNew={params.locationId === 'new'}
                  locations={currentCustomer.locations}
                  getLocations={getCurrentCustomerLocations}
                />
              </Col>
            )}
            <Col sm={mainContentWidth} className="industry-maincontent">
              <div>
                <Row>
                  <HelpWrapper
                    pdfUrl={
                      settings && settings.subAccountDetailHelpPdf
                        ? settings.subAccountDetailHelpPdf.value
                        : undefined
                    }
                    videoUrl={
                      settings && settings.subAccountDetailHelpVideo
                        ? settings.subAccountDetailHelpVideo.value
                        : undefined
                    }
                    extraClass={this.props.children ? 'child-page-present' : ''}
                  />
                </Row>
                {!this.props.children && (
                  <div>
                    <Link
                      to={`/${context}/${currentOrgId}/sub-accounts/new`}
                      className="well-300"
                    >
                      <Image src={FlagIcon} />
                      <Button bsStyle="primary">Create new Sub-Account</Button>
                    </Link>
                  </div>
                )}
              </div>
              {backUrl && (
                <Link to={backUrl} className="back-link">
                  &#11104; Back to customer
                </Link>
              )}
              {this.props.children && (
                <Row className="industries-details">
                  <Col sm={4} lg={3} className="industries-sidebar">
                    <LocationTabSidebar
                      activeTab={currentComponent}
                      locationId={params.locationId}
                      currentOrgId={params.customerId}
                      context={context}
                    />
                  </Col>
                  <Col sm={8} lg={9} className="industries-maincontent">
                    {React.cloneElement(this.props.children, { ...this.props })}
                  </Col>
                </Row>
              )}
            </Col>
          </Row>
        </Grid>
      );
    } else {
      return <LoadingSpinner />;
    }
  }
}

function mapStateToProps(state) {
  return {
    currentOrganisation: state.currentOrganisation,
    currentUser: state.currentUser,
    currentCustomer: state.currentCustomer,
    currentLocation: state.currentLocation,
    settings: state.settings
  };
}

function mapDispatchToProps(dispatch: Dispatch<*>) {
  return bindActionCreators(
    {
      ...userCreators,
      ...customerCreators,
      updateCurrentOrganisation,
      getSetting,
      getOrganisationUsers
    },
    dispatch
  );
}

export default connect(mapStateToProps, mapDispatchToProps)(Locations);



// WEBPACK FOOTER //
// ./src/containers/location/Locations.js